/** @file Validates a puppet document (design 4.1-4.3, 17) and returns it typed; throws with a path. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { BUILTIN_PROFILES } from '@zilo/pose3d-format/tables/builtinProfiles';
import { SECTOR_TABLES } from '@zilo/pose3d-format/tables/sectorTables';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { expectShape } from '@zilo/pose3d-format/validate/fields/expectShape';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { PUPPET_SHAPE } from '@zilo/pose3d-format/validate/shapes/puppetShape';
import { validateDrawables } from '@zilo/pose3d-format/validate/validateDrawables';
import { validateNodes } from '@zilo/pose3d-format/validate/validateNodes';
import { validateSectorTable } from '@zilo/pose3d-format/validate/validateSectorTable';

/** Profiles a puppet may name; default the built-ins. */
export type PuppetContext = { profiles?: Readonly<Record<string, Profile>> };

function checkSectorTables(puppet: Puppet): void {
  for (const [id, table] of Object.entries(puppet.sectorTables ?? {})) {
    const path = `/sectorTables/${id}`;
    if (Object.hasOwn(SECTOR_TABLES, id)) {
      throwError(ERROR_CODES.SECTOR_TABLE_SHAPE, { rule: 'a user table may not shadow a built-in', id }, { path });
    }
    if (table.id !== id) throwError(ERROR_CODES.SECTOR_TABLE_SHAPE, { rule: 'id equals its key', id }, { path: `${path}/id` });
    validateSectorTable(table, path);
  }
}

function checkSockets(puppet: Puppet): void {
  for (const [socket, node] of Object.entries(puppet.sockets)) {
    if (!Object.hasOwn(puppet.nodes, node)) throwError(ERROR_CODES.SOCKET_NODE_UNKNOWN, { socket, node }, { path: `/sockets/${socket}` });
  }
}

/** Every host node an attachment names (`nodeMap` values, `when`, `driver`) must exist; ids are unique. */
function checkAttachments(puppet: Puppet): void {
  const seen = new Set<string>();
  (puppet.attachments ?? []).forEach((a, i) => {
    const path = `/attachments/${i}`;
    if (seen.has(a.id)) failField(`${path}/id`, 'an id no other attachment has', a.id);
    seen.add(a.id);
    const refs: [string, string][] = Object.entries(a.nodeMap).map(([from, to]) => [`${path}/nodeMap/${from}`, to]);
    if (a.when !== undefined) refs.push([`${path}/when/hostNode`, a.when.hostNode], [`${path}/when/parent`, a.when.parent]);
    if (a.driver !== undefined) refs.push([`${path}/driver`, a.driver]);
    for (const [refPath, node] of refs) {
      if (!Object.hasOwn(puppet.nodes, node)) throwError(ERROR_CODES.ATTACHMENT_REF_UNKNOWN, { node }, { path: refPath, hint: 'a host node name' });
    }
  });
}

/** Returns `doc` as a `Puppet` when it is valid; otherwise throws the first error found. */
export function validatePuppet(doc: unknown, context: PuppetContext = {}): Puppet {
  const puppet = expectShape(doc, '', PUPPET_SHAPE) as Puppet;
  const profiles = context.profiles ?? BUILTIN_PROFILES;
  if (!Object.hasOwn(profiles, puppet.profile)) {
    throwError(ERROR_CODES.PROFILE_UNKNOWN, { profile: puppet.profile }, { path: '/profile', hint: Object.keys(profiles).join(', ') });
  }
  validateNodes(puppet.nodes);
  checkSockets(puppet);
  checkSectorTables(puppet);
  validateDrawables(puppet);
  checkAttachments(puppet);
  return puppet;
}
